import { ReactElement } from 'react'
import Add from '@mui/icons-material/Add'
import Cancel from '@mui/icons-material/Cancel'
import { IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { formatUtilization } from '../../MultiEditCheckSettings/components/CheckSettingsElementTable'
import { SupportCellState } from '../utils'

interface Props {
  state: SupportCellState
  /** Undefined while the member has no computable check for this support. */
  utilization?: number
  /** Removes the support from the edit, or adds a removed one back. */
  onToggle: () => void
  dataCy: string
}

/**
 * One support cell of the Auflagerpressung matrix. A support that takes part in
 * the edit shows its utilization and reveals the button that removes it while
 * the pointer is on the cell; a removed one collapses to a „+" that adds it
 * back. Supports the member does not really have stay empty — they are no part
 * of the edit at all.
 */
const SupportMatrixCell = ({
  state,
  utilization,
  onToggle,
  dataCy,
}: Props): ReactElement | null => {
  if (state === 'not-applicable') return null

  const available = utilization !== undefined && Number.isFinite(utilization)

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="center"
      width="100%"
      height="100%"
      sx={{
        '& .support-remove-button': { visibility: 'hidden' },
        '&:hover .support-remove-button': { visibility: 'visible' },
      }}
    >
      {state === 'removed' ? (
        <Tooltip title="Auflager wieder berücksichtigen">
          <IconButton size="small" sx={{ p: 0.25 }} onClick={onToggle} data-cy={`${dataCy}-add`}>
            <Add sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      ) : (
        <>
          <Typography
            sx={{ fontWeight: 'inherit', lineHeight: 1, display: 'flex', alignItems: 'center' }}
            data-cy={dataCy}
          >
            {available ? formatUtilization(utilization) : 'n/a'}
          </Typography>
          <IconButton
            className="support-remove-button"
            size="small"
            sx={{ p: 0.25 }}
            aria-label="Auflager entfernen"
            onClick={onToggle}
            data-cy={`${dataCy}-remove`}
          >
            <Cancel sx={{ fontSize: 16 }} />
          </IconButton>
        </>
      )}
    </Stack>
  )
}

export default SupportMatrixCell
